import type { ReactNode } from "react";

const WIDTH = {
	/** Dashboards and grids: use the whole screen. */
	full: "max-w-[1760px]",
	/** Forms and settings: wide enough for two columns, no wider. */
	wide: "max-w-6xl",
	/** Single-column reading. */
	narrow: "max-w-3xl",
} as const;

/**
 * Every page's frame: one gutter, one title treatment, actions on the right.
 * The title is set in the shelf-tag face so each page opens on the same
 * signature, and the count beside it (when a page has one) is the only
 * number in the header.
 */
export function Page({
	title,
	help,
	count,
	subtitle,
	actions,
	back,
	width = "full",
	children,
}: {
	title?: ReactNode;
	/** A HowItWorks icon, set beside the title. */
	help?: ReactNode;
	count?: ReactNode;
	subtitle?: ReactNode;
	actions?: ReactNode;
	back?: ReactNode;
	width?: keyof typeof WIDTH;
	children: ReactNode;
}) {
	return (
		<div className={`mx-auto w-full ${WIDTH[width]} px-3 pt-3 pb-8 sm:px-5 sm:pt-4 lg:px-6`}>
			{back && <div className="mb-2">{back}</div>}
			{(title || actions) && (
				<header className="mb-3 flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
					<div className="min-w-0">
						{title && (
							<h1 className="flex items-baseline gap-2.5 text-[22px] leading-7 font-bold font-tag">
								{title}
								{help && <span className="self-center font-sans">{help}</span>}
								{count != null && <span className="text-[13px] font-medium text-muted">{count}</span>}
							</h1>
						)}
						{subtitle && <p className="mt-0.5 text-[13px] text-muted">{subtitle}</p>}
					</div>
					{actions && <div className="flex shrink-0 items-center gap-1.5">{actions}</div>}
				</header>
			)}
			{children}
		</div>
	);
}

/** A section heading inside a page: sentence case, quiet, with room for a
 *  count or a control on the same line. */
export function SectionTitle({
	children,
	aside,
	className = "",
}: {
	children: ReactNode;
	aside?: ReactNode;
	className?: string;
}) {
	return (
		<div className={`mb-1.5 flex min-h-7 items-center gap-2 ${className}`}>
			<h2 className="text-[13px] font-semibold text-ink">{children}</h2>
			{aside}
		</div>
	);
}

/** A back link above a page title. */
export function BackLink({ href, children }: { href: string; children: ReactNode }) {
	return (
		<a href={href} className="inline-flex items-center gap-1 rounded-md py-0.5 text-xs text-muted hover:text-ink">
			<span aria-hidden>‹</span> {children}
		</a>
	);
}
